import { useEffect, useRef, useState } from 'react';
import { useCustom, useTranslate } from '@refinedev/core';
import { DateField } from '@refinedev/antd';
import { Button, Card, Table, Tag } from 'antd';
import { PlusOutlined, StopOutlined } from '@ant-design/icons';
import type { AppAdminContentCatalogSchemasPublisherContractContractOut as ContractOut } from '@/api/generated/schemas';
import { ContractFormModal } from '@/components/content_catalog/ContractFormModal';

interface PublisherContractsTabProps {
  publisherId: number;
  /** When the publisher was last fetched; a refetch reloads the contracts. */
  publisherFetchedAt: number;
  /** A signed or terminated contract changes the publisher's coverage. */
  onChanged: () => void;
}

export const PublisherContractsTab = ({
  publisherId,
  publisherFetchedAt,
  onChanged,
}: PublisherContractsTabProps) => {
  const t = useTranslate();
  const [modalOpen, setModalOpen] = useState(false);
  const [terminating, setTerminating] = useState<ContractOut | null>(null);

  const { query } = useCustom<ContractOut[]>({
    url: `content_catalog/publishers/${publisherId}/contracts`,
    method: 'get',
  });
  const contracts = query.data?.data ?? [];

  // Cascade publisher refreshes (refresh button, mutations) to the contracts list.
  const lastPublisherFetch = useRef(publisherFetchedAt);
  useEffect(() => {
    if (publisherFetchedAt !== lastPublisherFetch.current) {
      lastPublisherFetch.current = publisherFetchedAt;
      query.refetch();
    }
  }, [publisherFetchedAt, query]);

  const openModal = (contract: ContractOut | null) => {
    setTerminating(contract);
    setModalOpen(true);
  };

  return (
    <Card
      title={t('content_catalog.publishers.contracts.title')}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal(null)}
        >
          {t('content_catalog.publishers.contracts.sign.button')}
        </Button>
      }
    >
      <Table
        dataSource={contracts}
        loading={query.isLoading}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: t('content_catalog.publishers.contracts.empty'),
        }}
      >
        <Table.Column
          dataIndex="kind"
          title={t('content_catalog.publishers.contracts.fields.kind')}
          render={(value: string) => (
            <Tag>
              {t(`content_catalog.publishers.contracts.kind.${value}`, {
                defaultValue: value,
              })}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="effective_from"
          title={t(
            'content_catalog.publishers.contracts.fields.effective_from'
          )}
          render={(value: string) => <DateField value={value} format="ll" />}
        />
        <Table.Column
          dataIndex="effective_to"
          title={t('content_catalog.publishers.contracts.fields.effective_to')}
          render={(value: string | null) =>
            value ? <DateField value={value} format="ll" /> : '-'
          }
        />
        <Table.Column<ContractOut>
          title={t('base.fields.actions')}
          key="actions"
          width={140}
          render={(_, contract) =>
            contract.effective_to ? null : (
              <Button
                icon={<StopOutlined />}
                size="small"
                danger
                onClick={() => openModal(contract)}
              >
                {t('content_catalog.publishers.contracts.terminate.button')}
              </Button>
            )
          }
        />
      </Table>

      <ContractFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        publisherId={publisherId}
        contract={terminating}
        onSaved={onChanged}
      />
    </Card>
  );
};
